// Global Instructions Rule Applied!
//
// The expanded row of the Allocation grid (waterfall phase 6 PR 2b): the
// tier test of one investor, so a user can follow why its tiers hold what
// they hold. One line per tier - per tier and source on a Deal-by-Deal
// waterfall, where every source runs the tiers in turn - with the rates, what
// the target is made of, what had been received before, the headroom, what
// the tier absorbed now and how that splits, and the bucket after. On a
// waterfall that checks earlier distributions, the cumulative figures behind
// the Correction column sit beside it per class: entitled to date, received
// to date, correction.
//
// On a waterfall with pools (phase 6 PR 3) every line names its source and
// the source's pool, and the Cross-Pool Loss Offset tier's line says what it
// was tested on - the realized losses of the other pools not yet offset - and
// the pools its money went to.
//
// The figures are the engine's own tier-by-tier run, unrounded as it ran
// (wf__distribution_partners, from the frozen snapshot); the cumulative ones
// are the rows'. Nothing is computed here.

import React from 'react'
import { Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import NumberText from '../../../../core/components/NumberText.tsx'
import type {
  DistributionPartner,
  DistributionPartnerSource,
  DistributionPartnerTest
} from '../../../../state_manager/data_models/distribution_detail.ts'
import { TIER_METRIC_PROFIT_SHARE } from '../../../funds/components/forms/tabs/waterfallTierWording.ts'
import { CORRECTION_TITLE, METRIC_LOSS_OFFSET, fmtRate, hasFigure, tierStateTag, tierTitle } from './allocationLabels.ts'

const { Text } = Typography

const HISTORY_CHECK_NONE = 'none'
const dash = <Text type='secondary'>—</Text>

interface PartnerTierDetailProps {
  partner: DistributionPartner
  /** how earlier distributions were checked (cfg.waterfall_history_checks.code) */
  historyCheck: string | null | undefined
  dealByDeal: boolean
}

type TestRow = DistributionPartnerTest & { key: string }

const factor = (v: number | null | undefined): string => (v == null ? '?' : `${Math.round(v * 10000) / 10000}`)

export default function PartnerTierDetail({ partner, historyCheck, dealByDeal }: PartnerTierDetailProps) {
  const investor = partner.investors__name ?? 'this investor'
  const rows: TestRow[] = partner.tests.map((t, i) => ({ ...t, key: `${t.asset_transaction_id ?? 'fund'}-${t.tier_sequence}-${i}` }))
  const cumulative = historyCheck != null && historyCheck !== HISTORY_CHECK_NONE
  // a waterfall with pools: every test names the pool of its source
  const pooled = partner.tests.some((t) => t.pool_code != null)

  const classColumns = (cls: 'lp' | 'gp', title: string) => ({
    title,
    key: `cumulative-${cls}`,
    children: [
      {
        title: 'Entitled to date',
        key: `entitled-${cls}`,
        width: 140,
        align: 'right' as const,
        render: (_: unknown, t: TestRow) => <NumberText value={t[`entitled_${cls}` as const]} blank={null} />
      },
      {
        title: 'Received to date',
        key: `received-${cls}`,
        width: 140,
        align: 'right' as const,
        render: (_: unknown, t: TestRow) => <NumberText value={t[`received_${cls}` as const]} blank={null} />
      },
      {
        title: CORRECTION_TITLE,
        key: `correction-${cls}`,
        width: 130,
        align: 'right' as const,
        render: (_: unknown, t: TestRow) => {
          const correction = t[`correction_${cls}` as const]
          const carried = t[`carried_${cls}` as const]
          if (!hasFigure(correction) && !hasFigure(carried)) return null
          return (
            <div>
              {hasFigure(correction) ? <NumberText value={correction} /> : null}
              {hasFigure(carried) ? (
                <div>
                  <Text type='secondary'>
                    <NumberText value={carried} /> carried
                  </Text>
                </div>
              ) : null}
            </div>
          )
        }
      }
    ]
  })

  const columns: ColumnsType<TestRow> = [
    ...(dealByDeal || pooled
      ? [{ title: 'Source', key: 'source', width: 240, render: (_: unknown, t: TestRow) => t.source_label ?? dash }]
      : []),
    ...(pooled ? [{ title: 'Pool', key: 'pool', width: 90, render: (_: unknown, t: TestRow) => t.pool_code ?? dash }] : []),
    { title: 'Tier', key: 'tier', width: 200, render: (_: unknown, t) => tierTitle(t.tier_sequence, t.tier_type_label) },
    {
      title: 'Rate (partner / carry)',
      key: 'rate',
      width: 150,
      align: 'right',
      render: (_: unknown, t) => (t.rate_lp == null && t.rate_gp == null ? dash : `${fmtRate(t.rate_lp)} / ${fmtRate(t.rate_gp)}`)
    },
    {
      title: 'Basis',
      key: 'basis',
      width: 190,
      align: 'right',
      // what the target is made of: the threshold times the base, or the
      // catch-up's target share of what was paid under its tiers
      render: (_: unknown, t) =>
        !t.reached && t.remaining_before == null ? (
          dash
        ) : t.metric === METRIC_LOSS_OFFSET ? (
          // the realized losses of the other pools not yet offset, pool by pool
          t.remedied.length === 0 ? (
            dash
          ) : (
            <div>
              {t.remedied.map((r) => (
                <div key={r.pool_id}>
                  <Text type='secondary'>{r.pool_code ?? 'another pool'} </Text>
                  <NumberText value={r.unremedied} />
                </div>
              ))}
            </div>
          )
        ) : t.metric === TIER_METRIC_PROFIT_SHARE ? (
          <span>
            <Text type='secondary'>{fmtRate(t.r)} of </Text>
            <NumberText value={t.catch_up_total} />
          </span>
        ) : t.base_amount != null ? (
          <span>
            <Text type='secondary'>{factor(t.threshold)}× </Text>
            <NumberText value={t.base_amount} />
          </span>
        ) : (
          dash
        )
    },
    {
      title: 'Target',
      key: 'target',
      width: 160,
      align: 'right',
      render: (_: unknown, t) =>
        t.metric === TIER_METRIC_PROFIT_SHARE && t.catch_up != null ? (
          <span>
            <Text type='secondary'>catch-up </Text>
            <NumberText value={t.catch_up} />
          </span>
        ) : (
          <NumberText value={t.target} blank={dash} />
        )
    },
    {
      title: 'Received before',
      key: 'received_before',
      width: 150,
      align: 'right',
      render: (_: unknown, t) => (
        <NumberText value={t.metric === TIER_METRIC_PROFIT_SHARE ? t.catch_up_received : t.received_before} blank={dash} />
      )
    },
    { title: 'Headroom', key: 'headroom', width: 140, align: 'right', render: (_: unknown, t) => <NumberText value={t.headroom} blank={dash} /> },
    {
      title: 'Absorbed now',
      key: 'absorbed',
      width: 150,
      align: 'right',
      render: (_: unknown, t) => <NumberText value={t.absorbed} wrap={(f) => <Text strong>{f}</Text>} />
    },
    {
      title: 'Partner',
      key: 'partner',
      width: 150,
      align: 'right',
      render: (_: unknown, t) => (
        <div>
          <NumberText value={t.partner} />
          {/* the Cross-Pool Loss Offset tier: the pools whose capital the money returns */}
          {t.remedied
            .filter((r) => hasFigure(r.amount))
            .map((r) => (
              <div key={r.pool_id}>
                <Text type='secondary' className='text-xs'>
                  <NumberText value={r.amount} /> to {r.pool_code ?? 'another pool'}
                </Text>
              </div>
            ))}
        </div>
      )
    },
    { title: 'Carry', key: 'carry', width: 130, align: 'right', render: (_: unknown, t) => <NumberText value={t.carry} /> },
    {
      title: 'State after',
      key: 'state',
      width: 180,
      render: (_: unknown, t) => {
        const tag = tierStateTag(t.state_after)
        if (!tag) return null
        return (
          <Tag color={tag.color}>
            {t.state_after === 'open' && hasFigure(t.remaining) ? (
              <>
                <NumberText value={t.remaining} /> {tag.label}
              </>
            ) : t.state_after === 'open' ? (
              'Open'
            ) : (
              tag.label
            )}
          </Tag>
        )
      }
    },
    ...(cumulative ? [classColumns('lp', 'To partner'), classColumns('gp', 'Carry')] : [])
  ]

  const sourceColumns: ColumnsType<DistributionPartnerSource> = [
    { title: 'Source', dataIndex: 'label', render: (v: string | null | undefined) => v ?? dash },
    { title: 'To partner', dataIndex: 'partner_effective', width: 160, align: 'right', render: (v: number) => <NumberText value={v} /> },
    { title: 'Carry', dataIndex: 'carry_effective', width: 160, align: 'right', render: (v: number) => <NumberText value={v} /> },
    {
      title: 'Total',
      key: 'total',
      width: 160,
      align: 'right',
      render: (_: unknown, s) => <NumberText value={s.partner_effective + s.carry_effective} wrap={(f) => <Text strong>{f}</Text>} />
    }
  ]

  return (
    <div className='flex flex-col gap-3 py-1' data-testid={`tier-detail-${partner.fund_commitment_id}`}>
      {rows.length > 0 ? (
        <div>
          <Text type='secondary'>
            The tier test of {investor}: the tier-by-tier run of this distribution, each tier taking what the one before
            left.
          </Text>
          <Table<TestRow>
            size='small'
            rowKey='key'
            pagination={false}
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={rows}
            className='mt-1'
          />
        </div>
      ) : partner.fund_commitments__is_gp_commitment ? (
        <Text type='secondary'>The GP commitment&apos;s own share is paid to it as such: it runs through no tier.</Text>
      ) : (
        <Text type='secondary'>No tier tests for {investor} on this distribution.</Text>
      )}
      {dealByDeal && partner.sources.length > 0 ? (
        <div>
          <Text type='secondary'>By source, as the amounts stand</Text>
          <Table<DistributionPartnerSource>
            size='small'
            rowKey='asset_transaction_id'
            pagination={false}
            columns={sourceColumns}
            dataSource={partner.sources}
            className='mt-1'
          />
        </div>
      ) : null}
    </div>
  )
}
